import React from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";

const logos = [
  "/images/f1.webp",
  "/images/f2.webp",
  "/images/f3.webp",
  "/images/f4.webp",
  "/images/f5.webp",
  "/images/f6.webp",
];

const Footer = () => {
  return (
    <footer className="bg-[#C8F1F9] text-black px-6 pt-24 pb-10 overflow-hidden">

      {/* ===== HEADING ===== */}
      <motion.div
        initial={{ opacity: 0, y: 40 }}
        whileInView={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.9 }}
        viewport={{ once: true }}
        className="max-w-6xl mx-auto text-center mb-20"
      >
        <h3 className="text-3xl md:text-4xl lg:text-5xl font-extrabold text-[#3498db] leading-snug">
          Join Study Partners to prepare with confidence for india's most prestigious competitive examinations.
        </h3>
      </motion.div>

      {/* ===== LOGO SLIDER ===== */}
      <div className="relative max-w-7xl mx-auto mb-24 overflow-hidden">
        <div className="flex gap-12 w-max animate-footer-slide">
          {[...logos, ...logos].map((src, idx) => (
            <motion.div
              key={idx}
              whileHover={{ scale: 1.15 }}
              className="
                bg-black rounded-3xl p-7
                shadow-[0_20px_60px_rgba(0,0,0,0.35)]
                transition-all duration-500
              "
            >
              <img
                src={src}
                alt={`partner-${idx}`}
                className="
    h-24 sm:h-28 md:h-32 lg:h-36 xl:h-40
    w-auto
    object-contain
  "
              />
            </motion.div>
          ))}
        </div>
      </div>

      {/* ===== SOCIALS ===== */}
      <motion.div
        initial={{ opacity: 0, y: 30 }}
        whileInView={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.8 }}
        viewport={{ once: true }}
        className="text-center mb-20"
      >
        <h4 className="text-lg font-semibold text-blue-500 mb-10 tracking-wide">
          Know Us Better
        </h4>

        <div className="flex justify-center items-center gap-10 flex-wrap">
          {[
            {
              src: "/images/fb.png",
              link: "https://facebook.com",
              glow: "rgba(24,119,242,0.6)",
            },
            {
              src: "/images/insta.jpeg",
              link: "https://instagram.com",
              glow: "rgba(225,48,108,0.6)",
            },
            {
              src: "/images/y.png",
              link: "https://youtube.com",
              glow: "rgba(255,0,0,0.6)",
            },
          ].map((item, i) => (
            <motion.a
              key={i}
              href={item.link}
              target="_blank"
              rel="noopener noreferrer"
              initial={{ scale: 0.8, opacity: 0 }}
              whileInView={{ scale: 1, opacity: 1 }}
              transition={{ delay: i * 0.15, duration: 0.5 }}
              animate={{
                y: [0, -6, 0],
              }}
              whileHover={{
                scale: 1.25,
                boxShadow: `0 0 40px ${item.glow}`,
              }}

              className="
          relative
          w-16 h-16
          rounded-full
          bg-white
          flex items-center justify-center
          shadow-xl
          cursor-pointer
        "
            >
              {/* Glow Ring */}
              <span
                className="absolute inset-0 rounded-full blur-xl opacity-40"
                style={{ background: item.glow }}
              />

              {/* Icon */}
              <img
                src={item.src}
                alt="social"
                className="relative w-9 h-9 object-contain z-10"
              />
            </motion.a>
          ))}
        </div>
      </motion.div>


      {/* ===== DISCLAIMER ===== */}
      <motion.div
        initial={{ opacity: 0 }}
        whileInView={{ opacity: 1 }}
        transition={{ duration: 1 }}
        viewport={{ once: true }}
        className="max-w-5xl mx-auto text-sm text-gray-700 leading-relaxed mb-16 text-center"
      >
        <h5 className="font-bold text-[#2563EB] mb-4 text-lg">
          Disclaimer
        </h5>

        <p>
          Study Partners is an educational platform designed to support students
          through collaborative learning, concept discussions, and structured
          exam preparation.
        </p>

        <p className="mt-3">
          All content and study tools are provided for learning and informational
          purposes only and should be used responsibly.
        </p>

        <p className="mt-3">
          © Study Partners. All platform content and features are protected and
          dedicated solely to student learning.
        </p>
      </motion.div>

      {/* ===== BOTTOM ===== */}
      <div className="border-t border-gray-300 pt-6 max-w-7xl mx-auto flex flex-col md:flex-row justify-between items-center text-sm">
        <p className="text-red-500 mb-4 md:mb-0">
          © {new Date().getFullYear()} Study Partners | All rights reserved
        </p>
        <p className="text-red-500 text-xs mt-2 md:mt-0">
          Developed by :{" "}
          <a
            href="https://yashsoftsolution.com/"
            target="_blank"
            rel="noopener noreferrer"
            className="font-semibold text-[#3498db] hover:underline"
          >
            Yashsoft Solution
          </a>
        </p>


<div className="
  flex flex-col gap-4
  sm:flex-row sm:gap-6
">
  {/* About Us */}
  <Link to="/about" className="w-full sm:w-auto">
    <motion.button
      whileHover={{ scale: 1.08 }}
      whileTap={{ scale: 0.95 }}
      className="
        w-full sm:w-auto
        px-4 py-2
        sm:px-6 sm:py-2.5
        rounded-full
        bg-white
        border border-[#3498db]
        text-[#3498db]
        text-sm sm:text-base
        font-semibold
        shadow-md
        hover:bg-[#3498db]
        hover:text-white
        transition-all duration-300
      "
    >
      About Us
    </motion.button>
  </Link>

  {/* Privacy Policy */}
  <Link to="/privacy" className="w-full sm:w-auto">
    <motion.button
      whileHover={{ scale: 1.08 }}
      whileTap={{ scale: 0.95 }}
      className="
        w-full sm:w-auto
        px-4 py-2
        sm:px-6 sm:py-2.5
        rounded-full
        bg-white
        border border-[#3498db]
        text-[#3498db]
        text-sm sm:text-base
        font-semibold
        shadow-md
        hover:bg-[#3498db]
        hover:text-white
        transition-all duration-300
      "
    >
      Privacy Policy
    </motion.button>
  </Link>

  {/* Terms & Conditions */}
  <Link to="/terms" className="w-full sm:w-auto">
    <motion.button
      whileHover={{ scale: 1.08 }}
      whileTap={{ scale: 0.95 }}
      className="
        w-full sm:w-auto
        px-4 py-2
        sm:px-6 sm:py-2.5
        rounded-full
        bg-[#3498db]
        text-white
        text-sm sm:text-base
        font-semibold
        shadow-md
        hover:bg-[#217dbb]
        transition-all duration-300
      "
    >
      Terms & Conditions
    </motion.button>
  </Link>
</div>


      </div>

      {/* ===== SLIDER ANIMATION ===== */}
      <style>
        {`
          @keyframes footer-slide {
            from { transform: translateX(0); }
            to { transform: translateX(-50%); }
          }
          .animate-footer-slide {
            animation: footer-slide 35s linear infinite;
          }
        `}
      </style>

    </footer>
  );
};

export default Footer;
